import axiosInstance from '../config/axios.config';

/**
 * Existing Loan Service
 * Handles API calls for existing loan details management
 */
const existingLoanService = {
    /**
     * Calculate missing fields for a loan
     * Auto-calculates EMI, interest rate, principal, tenure, outstanding based on provided inputs
     * @param {Object} loanData - Loan details (any combination of fields)
     * @returns {Promise} Calculated loan data with all fields filled
     */
    calculate: async (loanData) => {
        const response = await axiosInstance.post('/offer-calculation/existing-loans/calculate', loanData);
        return response.data;
    },

    /**
     * Get existing loans for a lead
     * @param {string} leadId - Lead UUID
     * @returns {Promise} Loans with summary
     */
    getByLead: async (leadId) => {
        const response = await axiosInstance.get(`/offer-calculation/existing-loans/lead/${leadId}`);
        return response.data;
    },

    /**
     * Store a single existing loan for a lead
     * @param {string} leadId - Lead UUID
     * @param {Object} loanData - Loan object
     * @returns {Promise} Saved loan
     */
    store: async (leadId, loanData) => {
        const response = await axiosInstance.post(`/offer-calculation/existing-loans/lead/${leadId}`, loanData);
        return response.data;
    },

    /**
     * Store multiple existing loans for a lead (replaces existing)
     * @param {string} leadId - Lead UUID
     * @param {Array} loans - Array of loan objects
     * @returns {Promise} Saved loans with summary
     */
    storeBulk: async (leadId, loans) => {
        const response = await axiosInstance.post(`/offer-calculation/existing-loans/lead/${leadId}/bulk`, {
            loans,
        });
        return response.data;
    },

    /**
     * NON-DESTRUCTIVE partial upsert. Sends only the loans you want to
     * insert/update — does NOT delete sibling rows the payload omits.
     *
     * Use this when persisting a SUBSET of loans (e.g. auto-save during a
     * single doc upload that should touch only ONE loan without nuking
     * the other 6). The full-save "Save All" button still uses storeBulk.
     * Returns the same shape as storeBulk: { success, data: { loans, total_emi } }.
     */
    upsertLoans: async (leadId, loans) => {
        const response = await axiosInstance.post(
            `/offer-calculation/existing-loans/lead/${leadId}/upsert`,
            { loans }
        );
        return response.data;
    },

    /**
     * Update a single loan
     * @param {string} leadId - Lead UUID
     * @param {string} loanId - Loan ID
     * @param {Object} loanData - Updated loan details
     * @returns {Promise} Updated loan
     */
    update: async (leadId, loanId, loanData) => {
        const response = await axiosInstance.put(`/offer-calculation/existing-loans/lead/${leadId}/${loanId}`, loanData);
        return response.data;
    },

    /**
     * Delete a single loan
     * @param {string} leadId - Lead UUID
     * @param {string} loanId - Loan ID
     * @returns {Promise} Deletion result
     */
    delete: async (leadId, loanId) => {
        const response = await axiosInstance.delete(`/offer-calculation/existing-loans/lead/${leadId}/${loanId}`);
        return response.data;
    },

    /**
     * Toggle active status of a loan
     * @param {string} leadId - Lead UUID
     * @param {string} loanId - Loan ID
     * @returns {Promise} Updated loan
     */
    toggleActive: async (leadId, loanId) => {
        const response = await axiosInstance.post(`/offer-calculation/existing-loans/lead/${leadId}/${loanId}/toggle-active`);
        return response.data;
    },

    /**
     * Get total EMI for a lead
     * @param {string} leadId - Lead UUID
     * @returns {Promise} Total EMI data
     */
    getTotalEmi: async (leadId) => {
        const response = await axiosInstance.get(`/offer-calculation/existing-loans/lead/${leadId}/total-emi`);
        return response.data;
    },

    /**
     * Get loan type options
     * @returns {Promise} Loan type options
     */
    getLoanTypes: async () => {
        const response = await axiosInstance.get('/offer-calculation/existing-loans/loan-types');
        return response.data;
    },

    /**
     * Upload a supporting document for one existing loan.
     * Multipart POST — the backend stores the file on S3 and merges a
     * lightweight metadata entry into the loan's `documents` JSON column
     * under the given doc_type slug. Uploading the same doc_type again
     * REPLACES the prior copy (only the latest is kept per type).
     *
     * @param {string} leadId  Lead UUID
     * @param {string} loanId  Existing-loan numeric id
     * @param {string} docType One of: loan_sanction_letter | loan_agreement
     *                         | loan_repayment_schedule | loan_track_statement
     * @param {File}   file    Browser File from <input type="file"> / FileUpload
     * @returns {Promise<{ success, data: { doc_type, document, documents } }>}
     */
    uploadDocument: async (leadId, loanId, docType, file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('doc_type', docType);
        const response = await axiosInstance.post(
            `/offer-calculation/existing-loans/lead/${leadId}/${loanId}/documents`,
            formData,
            {
                headers: { 'Content-Type': 'multipart/form-data' },
                // Phase 2: backend runs OCR (Textract async + Claude) inline
                // after the file is persisted. Textract PDF polling can take
                // up to ~90s and Claude up to ~30s on top. Bump to 180s so
                // the upload doesn't axios-timeout while OCR is still going.
                // If OCR fails, backend still returns 200 with the file saved
                // and ocr_status='failed' — the frontend shows the manual-edit
                // hint, no retry needed.
                timeout: 180000,
            }
        );
        return response.data;
    },

    /**
     * Remove a previously uploaded supporting document for one loan.
     * Backend deletes the S3 object (best-effort) AND removes the entry
     * from the loan's documents JSON. Safe to call when nothing's uploaded.
     */
    deleteDocument: async (leadId, loanId, docType) => {
        const response = await axiosInstance.delete(
            `/offer-calculation/existing-loans/lead/${leadId}/${loanId}/documents/${docType}`
        );
        return response.data;
    },

    /**
     * Manually re-extract a previously-uploaded document.
     * Used when auto-OCR-on-upload failed (Textract timeout, Claude rate
     * limit, etc.) and the DSA wants another try without re-uploading the
     * file. Backend runs Textract+Claude again and writes the result into
     * the documents JSON; returns { success, data: { document } }.
     */
    ocrExtractDocument: async (leadId, loanId, docType) => {
        const response = await axiosInstance.post(
            `/offer-calculation/existing-loans/lead/${leadId}/${loanId}/documents/${docType}/ocr`,
            {},
            { timeout: 180000 }
        );
        return response.data;
    },
};

export default existingLoanService;
